import { get, postApi, put, delApi } from "@/shared/utils/fetchApi";
import { UserProfile } from "../types/profileTypes";

/**
 * Classe de serviço para encapsular todas as operações relacionadas aos perfis de usuário
 * Implementa o padrão Facade para simplificar as chamadas à API
 */
export class ProfileService {
  private static readonly BASE_URL = "/profiles";

  /**
   * Obtém todos os perfis de usuário
   * @returns Lista de perfis de usuário
   */
  static async getAll(): Promise<UserProfile[]> {
    try {
      // v2: `GET /users` (superadmin) retorna UserProfile[] flat. Legacy: `{data}`.

      return (await get<UserProfile[]>(`/users`)) ?? [];
    } catch (error) {
      console.error("Erro ao obter todos os perfis:", error);
      throw error;
    }
  }

  /**
   * Obtém um perfil de usuário pelo ID
   * @param id ID do perfil
   * @returns Perfil do usuário
   */
  static async getById(id: string): Promise<UserProfile> {
    try {
      // `GET /users/:id` retorna UserProfile flat (toAdminUserDto, com position + ai_*).
      // Legacy: `{data}`. Self ou admin/superadmin.

      return await get<UserProfile>(`/users/${encodeURIComponent(id)}`);
    } catch (error) {
      console.error(`Erro ao obter perfil com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Obtém todos os usuários da organização atual usando o token JWT
   * @returns Lista de perfis de usuário da organização atual
   */
  static async getCurrentOrganizationUsers(): Promise<UserProfile[]> {
    try {
      // `/users/organization/current` retorna UserProfile[] flat (sem envelope).
      // Legacy: `/profiles/organization/current/users` retorna `{data: UserProfile[]}`.

      return (await get<UserProfile[]>(`/users/organization/current`)) ?? [];
    } catch (error) {
      console.error("Erro ao obter usuários da organização atual:", error);
      throw error;
    }
  }

  /**
   * Obtém todos os perfis de uma organização
   * @param organizationId ID da organização
   * @returns Lista de perfis da organização
   */
  static async getByOrganization(
    organizationId: string,
  ): Promise<UserProfile[]> {
    try {
      // v2: `GET /users?organizationId=` flat. Legacy: `/profiles/organization/:id` `{data}`.

      return (
        (await get<UserProfile[]>(
          `/users?organizationId=${encodeURIComponent(organizationId)}`,
        )) ?? []
      );
    } catch (error) {
      console.error(
        `Erro ao obter perfis da organização ${organizationId}:`,
        error,
      );
      throw error;
    }
  }

  /**
   * Obtém todos os perfis com um status específico
   * @param status Status dos perfis (active, pending, etc)
   * @returns Lista de perfis com o status especificado
   */
  static async getByStatus(status: string): Promise<UserProfile[]> {
    try {
      // v2: `GET /users?status=` (superadmin) flat. Legacy: `/profiles/status/:s` `{data}`.

      return (await get<UserProfile[]>(`/users?status=${encodeURIComponent(status)}`)) ?? [];
    } catch (error) {
      console.error(`Erro ao obter perfis com status ${status}:`, error);
      throw error;
    }
  }

  /**
   * Obtém todos os perfis com uma função/papel específico
   * @param role Função/papel dos perfis (admin, user, etc)
   * @returns Lista de perfis com a função especificada
   */
  static async getByRole(role: string): Promise<UserProfile[]> {
    try {
      // v2: `GET /users?role=` (superadmin) flat. Legacy: `/profiles/role/:r` `{data}`.

      return (await get<UserProfile[]>(`/users?role=${encodeURIComponent(role)}`)) ?? [];
    } catch (error) {
      console.error(`Erro ao obter perfis com função ${role}:`, error);
      throw error;
    }
  }

  /**
   * Cria um novo perfil de usuário
   * @param profileData Dados do perfil a ser criado
   * @returns Perfil criado
   */
  // DEAD CODE (Gap #2): sem caller real + sem endpoint v2. Criação real = fluxo de invites.
  // Mantido só na legacy; será removido quando os hooks órfãos forem limpos.
  static async create(profileData: Partial<UserProfile>): Promise<UserProfile> {
    try {
      const result = await postApi<{ data: UserProfile }>(
        this.BASE_URL,
        profileData,
      );
      return result.data;
    } catch (error) {
      console.error("Erro ao criar perfil:", error);
      throw error;
    }
  }

  /**
   * Atualiza um perfil existente
   * @param id ID do perfil
   * @param profileData Dados do perfil a serem atualizados
   * @returns Perfil atualizado
   */
  static async update(
    id: string,
    profileData: Partial<UserProfile>,
  ): Promise<UserProfile> {
    try {
      // v2: `PUT /users/:id` retorna UserProfile flat (toAdminUserDto). Legacy: `{data}`.
      // Aceita role (admin), position/ai_* (self). Guards no backend.

      return await put<UserProfile>(`/users/${encodeURIComponent(id)}`, profileData);
    } catch (error) {
      console.error(`Erro ao atualizar perfil com ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Exclui um perfil
   * @param id ID do perfil a ser excluído
   * @returns Mensagem de sucesso
   */
  // DEAD CODE (Gap #2): sem caller real + sem endpoint v2. "Remover usuário" da UI = soft
  // delete (deactivate via PUT status:inactive). Hard delete de conta = userService.deleteUser.
  static async delete(id: string): Promise<string> {
    try {
      const result = await delApi<{ message: string }>(`${this.BASE_URL}/${encodeURIComponent(id)}`);
      return result.message;
    } catch (error) {
      console.error(`Erro ao excluir perfil com ID ${id}:`, error);
      throw error;
    }
  }
}
